import { LoginOutput, RefreshInput, RefreshOutput } from '@allocab/allocab-commons-shared-types';
import { differenceInMinutes } from 'date-fns';

import { updateTokens } from './auth';
import { fetchTokens } from './useTokens';
import { queryClient, queryKeys } from '../datastore';
import type { ApiClient } from '../network/types';

/** Marge avant expiration en dessous de laquelle on rafraîchit. */
const REFRESH_THRESHOLD_IN_MINUTES = 5;

/**
 * Fabrique le `freshTokensCallback` d'un client `Api`.
 *
 * Le rafraîchissement de token était écrit une fois par client — `driverApi`,
 * `boApi`, `passengerApi`, plus celui de `driver-app` — pour une seule et même
 * logique. Ce qui les différenciait n'était que **l'instance qui poste le
 * rafraîchissement**, d'où ce paramètre.
 *
 * `getApi` est une fonction et non une instance : elle est appelée au moment du
 * rafraîchissement, ce qui permet à un client de se passer lui-même en argument
 * sans dépendance circulaire à la construction.
 *
 * ⚠️ Le rafraîchissement doit partir de l'instance de l'app hôte, pas d'une
 * autre : c'est elle qui porte ses en-têtes et son traitement d'`INVALID_TOKEN`.
 */
export const createFreshTokensGetter = (getApi: () => ApiClient) => async (): Promise<string | null> => {
  // `ensureQueryData` et non `getQueryData` : celui-ci renvoie `undefined` tant
  // que `useTokens` n'a pas tourné, et la requête partait alors **sans bearer**.
  // Visible surtout sur `driver-app`, qui émet des requêtes avant tout rendu —
  // réveil par push, socket, suivi de position.
  //
  // ⚠️ Les trois options qui suivent ne sont pas décoratives, elles reprennent
  // celles de `useTokens`. `networkMode` surtout : au défaut `'online'`, quand
  // le cache est froid et l'appareil hors réseau, `ensureQueryData` retombe sur
  // `fetchQuery`, que react-query **met en pause** — la promesse ne résout
  // jamais et toute requête authentifiée reste bloquée. Lire le stockage ne
  // dépend pas du réseau : `'always'` est le seul mode correct ici.
  const tokens = await queryClient.ensureQueryData<LoginOutput | null>({
    queryKey: queryKeys.tokens(),
    queryFn: fetchTokens,
    networkMode: 'always',
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (!tokens) {
    return null;
  }

  if (differenceInMinutes(new Date(tokens.expires), Date.now()) < REFRESH_THRESHOLD_IN_MINUTES) {
    const data = await getApi().post<RefreshInput, RefreshOutput>(
      '/authentication/v1/token/refresh',
      { accessToken: tokens.accessToken, refreshToken: tokens.refreshToken },
      { skipAuth: true },
    );

    const refreshedTokens = {
      accessToken: data.accessToken,
      refreshToken: tokens.refreshToken,
      issued: data.issued,
      expires: data.expires,
    };

    // La session a pu changer pendant le POST : déconnexion (`clearTokens` a mis le
    // cache à `null`), ou un autre rafraîchissement concurrent déjà écrit. Écrire
    // quand même ressusciterait une session fermée — que `useTokens` repersisterait
    // aussitôt — ou écraserait des tokens plus récents. On ne persiste que si le
    // cache porte encore les tokens qu'on a rafraîchis ; la requête en cours, elle,
    // repart avec le token frais dans les deux cas.
    const currentTokens = queryClient.getQueryData<LoginOutput | null>(queryKeys.tokens());
    if (currentTokens?.accessToken === tokens.accessToken) {
      updateTokens(queryClient, refreshedTokens);
    }

    return refreshedTokens.accessToken;
  }

  return tokens.accessToken;
};
